import { ReactNode } from 'react';
import { Link, Stack, Typography } from '@mui/material';
import { ConnectionInstructionsType, InstructionsConfig, Platform } from './types';

interface ConnectionInstructionsConfig {
  title: string;
  description: string;
  showPortTab: boolean;
  startSessionTitle: ReactNode;
  startCommands: (
    instructionsConfig: InstructionsConfig,
    localPort?: number | null,
    instancePort?: number | null,
    shouldShowPortCommand?: boolean,
  ) => Record<Platform, string>;
}

const HDP_USER_GUIDE = '/HDP-User-Guide.pdf';

const connectionInstructionsConfig: Record<ConnectionInstructionsType, ConnectionInstructionsConfig> = {
  [ConnectionInstructionsType.WORKBENCH]: {
    title: 'Connection Instructions',
    description:
      'This workbench is intended to provide an SSH connection, but not through a client like PuTTY. To connect, please follow these steps.',
    showPortTab: true,
    startSessionTitle: <Typography variant="bodyDefaultStrong">2. Start the session</Typography>,
    startCommands: (
      instructionsConfig: InstructionsConfig,
      localPort?: number | null,
      instancePort?: number | null,
      shouldShowPortCommand?: boolean,
    ) => ({
      [Platform.WINDOWS]: `aws ssm start-session --target ${instructionsConfig.instanceId}${shouldShowPortCommand ? ` --document-name AWS-StartPortForwardingSession --parameters "portNumber"=["${instancePort}"],"localPortNumber"=["${localPort}"]` : ''}`,
      [Platform.LINUX]: `aws ssm start-session --target ${instructionsConfig.instanceId}${shouldShowPortCommand ? ` --document-name AWS-StartPortForwardingSession --parameters "portNumber=[${instancePort}],localPortNumber=[${localPort}]"` : ''}`,
      [Platform.MAC]: `aws ssm start-session --target ${instructionsConfig.instanceId}${shouldShowPortCommand ? ` --document-name AWS-StartPortForwardingSession --parameters "portNumber=[${instancePort}],localPortNumber=[${localPort}]"` : ''}`,
    }),
  },
  [ConnectionInstructionsType.DATASET]: {
    title: 'Dataset Connection Information',
    description: 'Use the role ARNs below to assume access and interact with the dataset S3 bucket.',
    showPortTab: false,
    startSessionTitle: (
      <Stack spacing={1}>
        <Typography variant="bodyDefaultStrong">
          2. Use S3 CLI commands to work with the S3 bucket listed below. For more information on available commands,
          run{' '}
          <Typography
            component="span"
            fontFamily="monospace"
            sx={{ backgroundColor: 'atmosphericWhite.main', borderRadius: 0.5 }}
          >
            aws s3 help
          </Typography>{' '}
          in the CLI or refer to the{' '}
          <Link color="electricBlue.main" href={HDP_USER_GUIDE} target="_blank" rel="noreferrer">
            User Guide
          </Link>
          .
        </Typography>
        <Typography>Bucket Name:</Typography>
      </Stack>
    ),
    startCommands: (instructionsConfig: InstructionsConfig) =>
      Object.values(Platform).reduce(
        (acc, platform) => {
          acc[platform] =
            `s3://${instructionsConfig?.bucketName || ''}${instructionsConfig?.bucketFolder ? `/${instructionsConfig.bucketFolder}` : ''}`;

          return acc;
        },
        {} as Record<Platform, string>,
      ),
  },
};

export default connectionInstructionsConfig;
